
/**
 * Validates JPEG magic bytes (FFD8FF)
 */
export const validateJPEGMagicBytes = async (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target?.result as ArrayBuffer).subarray(0, 3);
      const header = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
      resolve(header === 'ffd8ff'); // JPEG magic bytes
    };
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, 3));
  });
};

/**
 * Validates file extension matches allowed types
 */
export const validateFileExtension = (filename: string): boolean => {
  const ext = filename.toLowerCase().split('.').pop();
  return ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'webp';
};

/**
 * Sanitizes filename to prevent path traversal and special characters
 */
export const sanitizeFilename = (name: string): string => {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_').substring(0, 255);
};

/**
 * Basic synchronous validation (MIME type + size)
 */
export const validateImageFile = (file: File): boolean => {
  // Check MIME type
  if (!file.type.startsWith('image/jpeg') && !file.type.startsWith('image/jpg') && !file.type.startsWith('image/png') && !file.type.startsWith('image/webp')) {
    return false;
  }
  // Check file extension
  if (!validateFileExtension(file.name)) {
    return false;
  }
  // Check size limit (10MB)
  if (file.size > 10 * 1024 * 1024) {
    return false;
  }
  return true;
};

/**
 * Full async validation including magic bytes verification
 */
export const validateImageFileAsync = async (file: File): Promise<boolean> => {
  // First run basic synchronous checks
  if (!validateImageFile(file)) {
    return false;
  }
  // Then verify magic bytes
  return await validateJPEGMagicBytes(file);
};

export const getUniqueFiles = (newFiles: File[], existingFiles: File[]): File[] => {
  const existingNames = new Set(existingFiles.map(f => sanitizeFilename(f.name)));
  return newFiles.filter(file => !existingNames.has(sanitizeFilename(file.name)));
};

export const limitFiles = (files: File[], maxCount: number): File[] => {
  return files.slice(0, maxCount);
};

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};
